import { OrderCartV2Addon } from "../../services/redux/types/OrderCartV2Types";
import {
  AddonGroupSubItemType,
  AddonGroupType,
  CategoryType,
  ItemDetailsType,
  ItemType,
  SubItemType,
} from "../types";
import {
  addonGroupMatchesId,
  addonSubItemMatchesId,
  resolveAddonSubItemId,
  resolveAddonSubItemIdFromSubItem,
} from "./addonIds";
import { resolveAddonGroupUuid, resolveSubItemUuid } from "./entityKeys";

export type MenuLookups = {
  subItemsByUuid: Record<string, SubItemType>;
  addonSubItemsByUuid: Record<string, SubItemType>;
  addonGroupsByUuid: Record<string, AddonGroupType>;
};

export const emptyMenuLookups: MenuLookups = {
  subItemsByUuid: {},
  addonSubItemsByUuid: {},
  addonGroupsByUuid: {},
};

function subItemLookupUuid(subItem: SubItemType): string | undefined {
  return resolveSubItemUuid(subItem);
}

function cloneSubItemWithParent(
  subItem: SubItemType,
  item: ItemType
): SubItemType {
  return JSON.parse(
    JSON.stringify({
      ...subItem,
      item: {
        id: item.id,
        uuid: item.uuid,
        img_url: item.img_url,
        item_name: item.item_name,
        // Preserve the menu item version so the create-receipt body sends the
        // real version (outdated-item detection) instead of a hardcoded default.
        version: item.version,
        subItems: [],
      },
    })
  ) as SubItemType;
}

function indexAddonSubItem(
  subItem: SubItemType,
  into: Record<string, SubItemType>
) {
  const cloned = JSON.parse(JSON.stringify(subItem)) as SubItemType;
  const keys = new Set<string>();

  if (subItem.uuid) {
    keys.add(subItem.uuid);
  }
  if (subItem.id != null) {
    keys.add(String(subItem.id));
  }

  keys.forEach((key) => {
    into[key] = cloned;
  });
}

function indexAddonGroupsFromGroups(
  groups: AddonGroupType[] | undefined,
  into: Record<string, AddonGroupType>
) {
  for (const group of groups ?? []) {
    const key = resolveAddonGroupUuid(group);
    if (key) {
      into[key] = JSON.parse(JSON.stringify(group)) as AddonGroupType;
    }

    for (const addonGroupSubItem of group.addonGroupSubItem ?? []) {
      indexAddonGroupsFromGroups(
        addonGroupSubItem.subItem?.allAddonGroups,
        into
      );
    }
  }
}

function indexAddonSubItemsFromGroups(
  groups: AddonGroupType[] | undefined,
  into: Record<string, SubItemType>
) {
  for (const group of groups ?? []) {
    for (const addonGroupSubItem of group.addonGroupSubItem ?? []) {
      const subItem = addonGroupSubItem.subItem;
      if (!subItem) {
        continue;
      }

      indexAddonSubItem(subItem, into);
      indexAddonSubItemsFromGroups(subItem.allAddonGroups, into);
    }
  }
}

export function buildSubItemLookupFromCategories(
  categories: CategoryType[]
): Record<string, SubItemType> {
  const lookup: Record<string, SubItemType> = {};

  for (const category of categories) {
    for (const { item } of category.itemCategories ?? []) {
      for (const subItem of item.subItems ?? []) {
        const key = subItemLookupUuid(subItem);
        if (!key) {
          continue;
        }

        lookup[key] = cloneSubItemWithParent(subItem, item);
      }
    }
  }

  return lookup;
}

export function buildMenuLookupsFromCategories(
  categories: CategoryType[]
): MenuLookups {
  const items: ItemType[] = [];

  for (const category of categories) {
    for (const { item } of category.itemCategories ?? []) {
      items.push(item);
    }
  }

  return buildMenuLookupsFromItems(items);
}

/** Captures addon names from the cart mutation payload (same data legacy cart stored). */
export function buildMenuLookupsFromCartLineInput(input: {
  subItemDetails: SubItemType;
  itemDetails: ItemDetailsType;
  version?: number;
}): MenuLookups {
  const subItemsByUuid: Record<string, SubItemType> = {};
  const addonSubItemsByUuid: Record<string, SubItemType> = {};
  const addonGroupsByUuid: Record<string, AddonGroupType> = {};

  const version = input.version ?? input.subItemDetails.item?.version;

  const parentKey = subItemLookupUuid(input.subItemDetails);
  if (parentKey) {
    subItemsByUuid[parentKey] = JSON.parse(
      JSON.stringify({
        ...input.subItemDetails,
        item: {
          id: input.itemDetails.id,
          uuid: input.itemDetails.uuid,
          img_url: input.itemDetails.img_url,
          item_name: input.itemDetails.item_name,
          ...(version !== undefined ? { version } : {}),
          subItems: [],
        },
      })
    ) as SubItemType;
  }

  for (const group of input.subItemDetails.allAddonGroups ?? []) {
    indexAddonGroupsFromGroups([group], addonGroupsByUuid);

    for (const addonGroupSubItem of group.addonGroupSubItem ?? []) {
      if (addonGroupSubItem.subItem) {
        indexAddonSubItem(addonGroupSubItem.subItem, addonSubItemsByUuid);
      }

      indexAddonGroupsFromGroups(
        addonGroupSubItem.subItem?.allAddonGroups,
        addonGroupsByUuid
      );
      indexAddonSubItemsFromGroups(
        addonGroupSubItem.subItem?.allAddonGroups,
        addonSubItemsByUuid
      );
    }
  }

  return {
    subItemsByUuid,
    addonSubItemsByUuid,
    addonGroupsByUuid,
  };
}

export function resolveAddonNameFromLookups(
  subItemId: string,
  menuLookups: MenuLookups
): string {
  const fromFlatLookup = resolveAddonSubItemDisplayName(
    menuLookups.addonSubItemsByUuid[subItemId]
  );
  if (fromFlatLookup) {
    return fromFlatLookup;
  }

  for (const group of Object.values(menuLookups.addonGroupsByUuid)) {
    const template = group.addonGroupSubItem?.find((addonGroupSubItem) =>
      addonSubItemMatchesId(addonGroupSubItem, subItemId)
    );
    const fromGroup = resolveAddonSubItemDisplayName(template?.subItem);
    if (fromGroup) {
      return fromGroup;
    }
  }

  return "";
}

export function getAddonDisplayName(
  addonGroupSubItem: AddonGroupSubItemType,
  addonSubItemLookup: Record<string, SubItemType> = {},
  addonGroupLookup: Record<string, AddonGroupType> = {}
): string {
  const fromSubItem = resolveAddonSubItemDisplayName(addonGroupSubItem.subItem);
  if (fromSubItem) {
    return fromSubItem;
  }

  const subItemId =
    resolveAddonSubItemId(addonGroupSubItem) ??
    resolveAddonSubItemIdFromSubItem(addonGroupSubItem.subItem);

  if (subItemId) {
    const fromLookup = resolveAddonNameFromLookups(subItemId, {
      subItemsByUuid: {},
      addonSubItemsByUuid: addonSubItemLookup,
      addonGroupsByUuid: addonGroupLookup,
    });
    if (fromLookup) {
      return fromLookup;
    }
  }

  return "";
}

export function buildMenuLookupsFromItems(items: ItemType[]): MenuLookups {
  const subItemsByUuid: Record<string, SubItemType> = {};
  const addonSubItemsByUuid: Record<string, SubItemType> = {};
  const addonGroupsByUuid: Record<string, AddonGroupType> = {};

  for (const item of items) {
    for (const subItem of item.subItems ?? []) {
      const key = subItemLookupUuid(subItem);
      if (key) {
        subItemsByUuid[key] = cloneSubItemWithParent(subItem, item);
      }

      indexAddonGroupsFromGroups(subItem.allAddonGroups, addonGroupsByUuid);
      indexAddonSubItemsFromGroups(subItem.allAddonGroups, addonSubItemsByUuid);
    }
  }

  return {
    subItemsByUuid,
    addonSubItemsByUuid,
    addonGroupsByUuid,
  };
}

export function mergeSubItemRecords(
  base: Record<string, SubItemType>,
  patch: Record<string, SubItemType>
): Record<string, SubItemType> {
  const merged = { ...base };

  for (const [key, patchSubItem] of Object.entries(patch)) {
    const existingVersion = base[key]?.item?.version;
    const patchVersion = patchSubItem.item?.version;

    // Menu item versions only increase on the backend, so the higher of the
    // two is the current one. The patch entry may come from persisted state
    // or ITEMS_DATA local storage and must never downgrade the version below
    // a fresher menu fetch — create-receipt sends this version and the
    // backend rejects outdated ones (ReqItemsContainsOutDatedItems).
    if (
      existingVersion !== undefined &&
      patchSubItem.item &&
      (patchVersion === undefined || patchVersion < existingVersion)
    ) {
      merged[key] = {
        ...patchSubItem,
        item: { ...patchSubItem.item, version: existingVersion },
      };
    } else {
      merged[key] = patchSubItem;
    }
  }

  return merged;
}

export function mergeRecordMaps<T>(
  base: Record<string, T>,
  patch: Record<string, T>
): Record<string, T> {
  return { ...base, ...patch };
}

function buildSelectedAddonFromLookup(
  addon: {
    quantity: number;
    unitPrice: number;
    unitPriceBeforeDiscount?: number;
    addonGroupSubItemId?: number;
  },
  addonSubItem: SubItemType
): AddonGroupSubItemType {
  return {
    // Never fabricate the row id from the SUB-ITEM id: the receipt endpoint
    // resolves addons[].id against addon_group_sub_item rows, so a sub-item id
    // either 452s the order or — worse — silently matches a different option.
    // Use the row id persisted on the line at add-to-cart time; when absent
    // (legacy persisted lines), 0 fails loudly at the backend instead.
    id: addon.addonGroupSubItemId ?? 0,
    basePrice: addon.unitPrice,
    price: addon.unitPrice,
    previousPrice: addon.unitPriceBeforeDiscount ?? addon.unitPrice,
    count: addon.quantity,
    subItem: JSON.parse(JSON.stringify(addonSubItem)) as SubItemType,
  };
}

function findAddonGroupTemplate(
  addonGroupId: string,
  parentTemplates: SubItemType,
  menuLookups: MenuLookups
): AddonGroupType | undefined {
  return (
    parentTemplates.allAddonGroups?.find((group) =>
      addonGroupMatchesId(group, addonGroupId)
    ) ?? menuLookups.addonGroupsByUuid[addonGroupId]
  );
}

function findAddonGroupSubItemTemplate(
  addon: OrderCartV2Addon,
  groupTemplate: AddonGroupType | undefined
): AddonGroupSubItemType | undefined {
  return groupTemplate?.addonGroupSubItem?.find((addonGroupSubItem) =>
    addonSubItemMatchesId(addonGroupSubItem, addon.subItemId)
  );
}

function findAddonGroupSubItemAcrossLookups(
  subItemId: string,
  menuLookups: MenuLookups
): AddonGroupSubItemType | undefined {
  for (const group of Object.values(menuLookups.addonGroupsByUuid)) {
    const found = group.addonGroupSubItem?.find((addonGroupSubItem) =>
      addonSubItemMatchesId(addonGroupSubItem, subItemId)
    );
    if (found) {
      return found;
    }
  }

  return undefined;
}

export function resolveAddonSubItemDisplayName(subItem?: SubItemType): string {
  if (!subItem) {
    return "";
  }

  return (
    subItem.name?.trim() ||
    subItem.fullName?.trim() ||
    subItem.nameLocalized?.trim() ||
    subItem.fullNameLocalized?.trim() ||
    ""
  );
}

function enrichAddonSubItemFromLookup(
  subItem: SubItemType,
  lookupSubItem?: SubItemType
): SubItemType {
  const resolvedName =
    resolveAddonSubItemDisplayName(subItem) ||
    resolveAddonSubItemDisplayName(lookupSubItem);

  return {
    ...subItem,
    name: resolvedName || subItem.name,
    fullName:
      subItem.fullName ||
      lookupSubItem?.fullName ||
      resolvedName ||
      subItem.name,
    nameLocalized: subItem.nameLocalized || lookupSubItem?.nameLocalized,
    fullNameLocalized:
      subItem.fullNameLocalized || lookupSubItem?.fullNameLocalized,
  };
}

export function resolveSelectedAddonFromLine(
  addon: OrderCartV2Addon,
  parentTemplates: SubItemType,
  menuLookups: MenuLookups
): AddonGroupSubItemType | undefined {
  const lookupSubItem = menuLookups.addonSubItemsByUuid[addon.subItemId];
  const groupTemplate = findAddonGroupTemplate(
    addon.addonGroupId,
    parentTemplates,
    menuLookups
  );
  const template =
    findAddonGroupSubItemTemplate(addon, groupTemplate) ??
    findAddonGroupSubItemAcrossLookups(addon.subItemId, menuLookups) ??
    (lookupSubItem
      ? buildSelectedAddonFromLookup(addon, lookupSubItem)
      : undefined) ??
    buildSelectedAddonFromLookup(addon, {
      uuid: addon.subItemId,
      name: resolveAddonSubItemDisplayName(lookupSubItem),
      fullName: lookupSubItem?.fullName,
      nameLocalized: lookupSubItem?.nameLocalized,
      fullNameLocalized: lookupSubItem?.fullNameLocalized,
      price: addon.unitPrice,
      basePrice: addon.unitPrice,
      previousPrice: addon.unitPriceBeforeDiscount ?? addon.unitPrice,
      customFranchiseSubItemPrices: [],
    });

  const selected = JSON.parse(
    JSON.stringify(template)
  ) as AddonGroupSubItemType;
  // The row id persisted at add-to-cart time is authoritative — templates can
  // be stale or hold a sibling option (lookups keep one template per sub-item,
  // overwritten by each add of that sub-item).
  if (addon.addonGroupSubItemId != null) {
    selected.id = addon.addonGroupSubItemId;
  }
  selected.count = addon.quantity;
  selected.price = addon.unitPrice;
  selected.basePrice = addon.unitPrice;
  if (addon.unitPriceBeforeDiscount) {
    selected.previousPrice = addon.unitPriceBeforeDiscount;
  }

  if (selected.subItem) {
    const resolvedName =
      resolveAddonSubItemDisplayName(selected.subItem) ||
      addon.displayName ||
      resolveAddonNameFromLookups(addon.subItemId, menuLookups);

    selected.subItem = enrichAddonSubItemFromLookup(
      {
        ...selected.subItem,
        name: resolvedName || selected.subItem.name || "",
        fullName:
          selected.subItem.fullName ||
          resolvedName ||
          selected.subItem.name ||
          "",
      },
      lookupSubItem
    );
  }

  return selected;
}

export function ensureAddonGroupOnSubItem(
  subItem: SubItemType,
  addonGroupId: string,
  parentTemplates: SubItemType,
  menuLookups: MenuLookups
): AddonGroupType | undefined {
  const existing = subItem.allAddonGroups?.find((group) =>
    addonGroupMatchesId(group, addonGroupId)
  );
  if (existing) {
    return existing;
  }

  const template = findAddonGroupTemplate(
    addonGroupId,
    parentTemplates,
    menuLookups
  );
  if (!template) {
    return undefined;
  }

  const group = JSON.parse(
    JSON.stringify({ ...template, addonGroupSubItem: [] })
  ) as AddonGroupType;

  if (!subItem.allAddonGroups) {
    subItem.allAddonGroups = [];
  }
  subItem.allAddonGroups.push(group);

  return group;
}
